import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import Pane from 'components/Common/Pane'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import type { ComponentProps } from 'react'
import React, { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import HeroImage from 'content/components/HeroImage'
import BodyText from 'LuxKit/Typography/BodyText'
import LineCommentsAltIcon from 'LuxKit/Icons/line/LineCommentsAltIcon'
import LineGraphBarIcon from 'LuxKit/Icons/line/LineGraphBarIcon'
import ModalContext from 'contexts/ModalContext'
import AgentHubExpressionOfInterestModal from 'agentHub/components/AgentHubExpressionOfInterestModal'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import OfferTypesCarousel from 'home/components/BrowseOfferTypes/OfferTypesCarousel'
import PartnerCarousel from 'components/Common/PartnerBrands/PartnerCarousel'
import LineAwardIcon from 'LuxKit/Icons/line/LineAwardIcon'
import AgentHubOrderedSellingPointsTile from './AgentHubOrderedSellingPointsTile'
import ValuePropositionItems from 'home/components/ValuePropositionBanner/ValuePropositionItems'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP } from 'agentHub/routes/AgentHubRoutes'
import useAgentHubAgentStatus from 'agentHub/hooks/useAgentHubAgentStatus'
import qs from 'qs'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'

const HeroImageContainer = styled(HeroImage)`
  padding: ${rem(180)} ${rem(20)} ${rem(20)} ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(160)} ${rem(16)};
  }
`

const TopHeroContainer = styled(LayoutContainer)`
  position: relative;
  padding: 0;
`

const HeroImageHeading = styled(Heading)`
  max-width: ${rem(600)};
`

const HeroImageSubtitlesContainer = styled(Group)`
  max-width: ${rem(600)};
`

const StyledImage = styled(ResponsiveImage)`
  max-width: ${rem(530)};
`

const AdvantagesContainer = styled(LayoutContainer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(60)};
    padding-bottom: ${rem(60)};
  }
`

const AdvantagesContainer2 = styled(LayoutContainer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(80)};
    padding-bottom: ${rem(80)};
  }
`

const PartnerBrandsContainer = styled(LayoutContainer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(40)};
    padding-bottom: ${rem(40)};
  }
`

const OfferTypesCarouselContainer = styled(Group)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};

  ${mediaQueryUp.desktop} {
    padding-top: ${rem(60)};
    padding-bottom: ${rem(60)};
  }
`

function AgentHubLandingPage() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const toggleModal = useCallback(() => {
    showModal(<AgentHubExpressionOfInterestModal />)
  }, [showModal])

  const loggedIn = useAppSelector(selectLoggedIn)
  const { token } = useQsQueryParams<{ token: string }>()
  const agent = useAgentHubAgentStatus({ registrationToken: token })
  const agentHubAccount = useAppSelector(state => state.agentHub.agentHubAccount)

  const canContinueAccountCreation = loggedIn && agentHubAccount.account?.status === 'pending'

  const continueOnboardingParams = qs.stringify({
    // TODO: fetch actual registration token instead of agentId
    token: agent.data?.agentId || agentHubAccount.account?.agentId,
  })

  const sellingPoints = [
    {
      key: 'clientSavings',
      icon: <LineAwardIcon />,
      heading: intl.formatMessage({
        id: '5BouwpoDyg',
        defaultMessage: 'Client Savings',
        description: 'Selling point heading about savings for travel agent clients',
      }),
      body: intl.formatMessage({
        id: 'bh+Sxu+sY1',
        defaultMessage: 'Unlock Luxury Escapes travel deals for your clients, with commission on all bookings',
        description: 'Selling point description about unlocking travel deals with commission',
      }),
      show: true,
    },
    {
      key: 'bookingManagement',
      icon: <LineGraphBarIcon />,
      heading: intl.formatMessage({
        id: '7ch2hAvUaz',
        defaultMessage: 'Booking Management',
        description: 'Selling point heading about booking management features',
      }),
      body: intl.formatMessage({
        id: 'LYBEHh0g+8',
        defaultMessage: 'Easily manage bookings with live status updates and self-service dashboards',
        description: 'Selling point description about booking management capabilities',
      }),
      show: true,
    },
    {
      key: 'worldwideInventory',
      icon: <LineGlobeAltIcon />,
      heading: intl.formatMessage({
        id: '05DRT4biSx',
        defaultMessage: 'Worldwide Inventory',
        description: 'Selling point heading about worldwide travel inventory access',
      }),
      body: intl.formatMessage({
        id: '4Y+pOYsjCG',
        defaultMessage: 'Access exclusive rates from directly contracted partners all over the world',
        description: 'Selling point description about accessing worldwide exclusive rates',
      }),
      show: true,
    },
    {
      key: 'agentSupport',
      icon: <LineCommentsAltIcon />,
      heading: intl.formatMessage({
        id: 'yfk77VdvtV',
        defaultMessage: '24/7 Agent Support',
        description: 'Selling point heading about round-the-clock agent support',
      }),
      body: intl.formatMessage({
        id: 'N06SSIAFFP',
        defaultMessage: 'Expert round-the-clock support for all bookings and enquiries',
        description: 'Selling point description about 24/7 expert support for agents',
      }),
      show: true,
    },
  ]

  const orderedSellingPoints: Array<ComponentProps<typeof AgentHubOrderedSellingPointsTile>> = [
    {
      title: intl.formatMessage({
        id: '7VSqe4AKEq',
        defaultMessage: 'Register your interest now',
        description: 'First step title in agent hub onboarding process',
      }),
      description: intl.formatMessage({
        id: 'njtlI1lMOC',
        defaultMessage: 'Be the first to join Luxury Escapes {displayShortName}.',
        description: 'Description of the first step in agent hub onboarding process',
      }, {
        displayShortName: agentHubOrSubBrandConfig.displayShortName,
      }),
      itemNumber: 1,
    },
    {
      title: intl.formatMessage({
        id: '1WkeCb5VrQ',
        defaultMessage: 'Get verified',
        description: 'Second step title in agent hub onboarding process',
      }),
      description: intl.formatMessage({
        id: 'Vl+Y3+bO4C',
        defaultMessage: 'Undergo a quick and secure verification process to confirm your status as a travel agent or agency.',
        description: 'Description of the verification step in agent hub onboarding',
      }),
      itemNumber: 2,
    },
    {
      title: intl.formatMessage({
        id: 'Qi7kMxERvX',
        defaultMessage: 'Start booking, start earning',
        description: 'Third step title in agent hub onboarding process',
      }),
      // TODO: i18n - concatenated string with displayShortName possessive
      description: `Boost your business with every successful booking, thanks to ${agentHubOrSubBrandConfig.displayShortName}'s competitive commission structure.`,
      itemNumber: 3,
    },
  ]

  const heroImageSubtitles = [
    intl.formatMessage({
      id: '+vxgJBfry5',
      defaultMessage: 'The only way to get commissionable access to Luxury Escapes Exclusive travel offers',
      description: 'Hero subtitle about exclusive commissionable access for agents',
    }),
    intl.formatMessage({
      id: 'jEcQFnjdNF',
      defaultMessage: 'Find and book agent-only packages and hidden deals not available to other consumers',
      description: 'Hero subtitle about agent-only exclusive deals',
    }),
    intl.formatMessage({
      id: 'JOTtRd4/7Y',
      defaultMessage: 'Competitive commissions on travel bookings',
      description: 'Hero subtitle about competitive commission rates for agents',
    }),
    intl.formatMessage({
      id: 'ipcC0jSGCx',
      defaultMessage: 'Manage instant bookings in your own custom dashboard',
      description: 'Hero subtitle about custom booking management dashboard',
    }),
  ]

  return <>
    <Pane>
      <HeroImageContainer
        brightness={-35}
        gravity="south"
        quality="best"
        tabletAspectRatio="2:1"
        mobileAspectRatio="2:1"
        desktopAspectRatio="16:9"
        id="3or29n7ploh4qriq852j"
        fit="center"
      >
        <TopHeroContainer>
          <Group direction="vertical" gap={20} tabletHorizontalAlign="start">
            <HeroImageHeading variant="heading1" colour="neutral-eight">
              {intl.formatMessage({
                id: 'o5ydI0qu74',
                defaultMessage: "Earn commission on the world's best travel deals",
                description: 'Hero heading on agent hub landing page about earning commission on travel deals',
              })}
            </HeroImageHeading>

            <HeroImageSubtitlesContainer direction="vertical" gap={4}>
              {heroImageSubtitles.map((item) => (
                <BodyTextBlock
                  key={item}
                  variant="large"
                  colour="neutral-eight"
                  startIcon={<LineCheckIcon />}
                >
                  {item}
                </BodyTextBlock>
              ))}
            </HeroImageSubtitlesContainer>

            <Group direction="vertical" tabletDirection="horizontal" gap={12}>
              <TextButton
                kind="primary"
                variant="ghost"
                size="large"
                onClick={toggleModal}
              >
                {intl.formatMessage({
                  id: 'LHe7GVa4LK',
                  defaultMessage: 'Register your interest',
                  description: 'Button to register interest in agent hub program',
                })}
              </TextButton>
              {canContinueAccountCreation && <TextButton
                kind="primary"
                variant="dark"
                size="large"
                to={`${LE_AGENT_HUB_COMPLETE_ACCOUNT_SETUP}?${continueOnboardingParams}`}
              >
                {intl.formatMessage({
                  id: '/PCEIrfLCT',
                  defaultMessage: 'Continue onboarding',
                  description: 'Button to continue agent hub account onboarding process',
                })}
              </TextButton>}
            </Group>
          </Group>
        </TopHeroContainer>
      </HeroImageContainer>
    </Pane>

    <Pane>
      <AdvantagesContainer>
        <Group direction="vertical" gap={32}>
          <Group direction="vertical" gap={8}>
            <Heading variant="heading2">
              <FormattedMessage
                id="uKEJQxIytZ"
                defaultMessage="{name} takes your business to the <i>next level</i>"
                description="Section heading about the agent hub brand improving business"
                values={{
                  name: agentHubOrSubBrandConfig.displayShortName,
                  i: (chunks: React.ReactNode) => <i>{chunks}</i>,
                }}
              />
            </Heading>
            <BodyText variant="large">
              {intl.formatMessage({
                id: 'x9Atxg3Noq',
                defaultMessage: 'Savings for your clients, commission in your pocket',
                description: 'Body text about client savings and agent commission on agent hub landing page',
              })}
            </BodyText>
          </Group>

          <ValuePropositionItems values={sellingPoints} />
        </Group>
      </AdvantagesContainer>
    </Pane>

    <Pane background="neutral-seven">
      <AdvantagesContainer2>
        <Group horizontalAlign="space-between" desktopDirection="horizontal" direction="vertical" desktopGap={80} gap={32}>
          <Group direction="vertical" gap={16}>
            <Group direction="vertical" gap={4}>
              <Heading variant="heading2">
                {intl.formatMessage({
                  id: 'kdp2CQWvzW',
                  defaultMessage: 'Ready, set, earn',
                  description: 'Section heading for getting started with agent hub commissions',
                })}
              </Heading>
              <BodyText variant="large">
                {intl.formatMessage({
                  id: 'oFBCyxOeQc',
                  defaultMessage: 'Get verified and kickstart your commissions',
                  description: 'Body text encouraging agents to get verified to start earning',
                })}
              </BodyText>
            </Group>

            <Group direction="vertical" gap={16}>
              {orderedSellingPoints.map(item => (
                <AgentHubOrderedSellingPointsTile
                  key={item.title}
                  description={item.description}
                  title={item.title}
                  itemNumber={item.itemNumber}
                />
              ))}
            </Group>
          </Group>
          <StyledImage id="2n8iffkn0nxnmtw4914" />
        </Group>
      </AdvantagesContainer2>
    </Pane>

    <Pane>
      <OfferTypesCarouselContainer direction="vertical" gap={20}>
        <LayoutContainer>
          <Heading variant="heading2">
            {intl.formatMessage({
              id: 'XlJzmuICxf',
              defaultMessage: "The world's best travel deals all in one place",
              description: 'Section heading about browsing travel deals on agent hub landing page',
            })}
          </Heading>
        </LayoutContainer>
        <OfferTypesCarousel disableActionButton />
      </OfferTypesCarouselContainer>
    </Pane>

    <Pane>
      <PartnerBrandsContainer>
        <Group direction="vertical" gap={16}>
          <Group direction="vertical" gap={8}>
            <Heading variant="heading2">
              {intl.formatMessage({
                id: 'EFc6BOEfJ3',
                defaultMessage: 'We partner with the world’s best brands',
                description: 'Section heading about partnering with top travel brands on agent hub landing page',
              })}
            </Heading>
            <BodyText variant="large">
              {intl.formatMessage({
                id: 'WxJJ8xA0bc',
                defaultMessage: 'Access premier brands with thousands of the best hotels, tours and cruises at your fingertips',
                description: 'Body text about accessing premier travel brand inventory on agent hub landing page',
              })}
            </BodyText>
          </Group>
          <PartnerCarousel />
        </Group>
      </PartnerBrandsContainer>
    </Pane>

  </>
}

export default AgentHubLandingPage
